import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, updateData, deleteData, activateData, postData } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { usePermissions } from '../../../usePermissions';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Box,
  Snackbar,
  Alert,
  Chip,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  FilterList,
  Refresh,
  Assignment,
  Edit as EditIcon,
  Warning,
  CheckCircle,
  Add,
  Print as PrintIcon
} from '@mui/icons-material';
import SaleReturnModal from './SaleReturnModal';
import { printSaleReturnPDF } from './SaleReturnPdfGenerator';
import '../Sale/SaleIndex.css';

const SaleReturnAgainstIndex = () => {
  // Session validation
  useSessionValidation();

  const { token, companyId, branchId, userId } = useAuth();
  const { canCreateTransaction, canEditTransaction, canDeleteTransaction } = usePermissions();

  // Mobile detection
  const isMobile = useMediaQuery('(max-width:600px)');

  // State management
  const [SaleReturns, setSaleReturns] = useState([]);
  const [customers, setcustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [returnDetails, setReturnDetails] = useState([]);
  const [editFormData, setEditFormData] = useState({});
  const [vatType, setVATType] = useState(1); // 1 = Excluding Tax, 2 = Including Tax
  const [editReturnDetails, setEditReturnDetails] = useState([]);

  // Cancel confirmation modal for Edit
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalEditData, setOriginalEditData] = useState(null);
  const [originalEditDetails, setOriginalEditDetails] = useState([]);

  // Delete confirmation modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemInfo, setDeleteItemInfo] = useState({ billNumber: '', customer: '' });

  // Activate confirmation modal
  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemInfo, setActivateItemInfo] = useState({ billNumber: '', customer: '' });

  // Create Sale Return modal
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Print state
  const [printingItemId, setPrintingItemId] = useState(null);
  const [items, setItems] = useState([]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch customers
  const fetchcustomers = async () => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token);
      const customerData = response.response_data || response || [];
      setcustomers(Array.isArray(customerData) ? customerData : []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setcustomers([]);
    }
  };

  // Fetch Sale Returns (Active and Inactive)
  const fetchSaleReturns = async () => {
    setLoading(true);
    setLoadingMessage('Fetching Sale returns...');
    try {
      // Fetch Active Sale Returns
      const activeResponse = await fetchData('SaleReturnApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({
        ...item,
        IsActive: item.IsActive ?? true
      }));

      // Fetch Inactive Sale Returns
      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('SaleReturnApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({
          ...item,
          IsActive: false
        }));
      } catch (inactiveError) {
        console.error('Error fetching inactive Sale returns:', inactiveError);
      }

      setSaleReturns([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching Sale returns:', error);
      setError('Failed to fetch Sale returns');
      setSaleReturns([]);
    }
    setLoading(false);
  };

  // Fetch inactive Sale Return details for view modal
  const fetchInactiveSaleReturnDetails = async (id) => {
    try {
      const response = await fetchData('SaleReturnApi', `/InActiveList/${id}/${branchId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching inactive Sale return details:', error);
      return null;
    }
  };

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchcustomers();
      fetchSaleReturns();
      fetchItems();
    }
  }, [companyId, token, fromDate, toDate]);

  // Fetch items for PDF generation
  const fetchItems = async () => {
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      const itemsData = response.response_data || response || [];
      setItems(Array.isArray(itemsData) ? itemsData : []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  // Fetch single Sale Return details
  const fetchSingleSaleReturn = async (saleReturnId) => {
    try {
      const response = await fetchData('SaleReturnApi', `/${saleReturnId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching single Sale Return:', error);
      return null;
    }
  };

  // Print handler
  const handlePrint = async (saleReturnId) => {
    setPrintingItemId(saleReturnId);
    try {
      // Fetch sale return details
      const freshData = await fetchSingleSaleReturn(saleReturnId);

      if (!freshData) {
        setError('Failed to fetch sale return details.');
        setPrintingItemId(null);
        return;
      }

      const saleReturnData = freshData.SaleReturn || freshData;
      const details = freshData.SaleReturnDetails || [];

      // Fetch branch details for company information
      const branchResponse = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      const branchData = branchResponse.responseCode == '1111' ? (branchResponse.response_data || {}) : {};

      const companyDetails = {
        companyName: branchData.CompanyName,
        address: `${branchData.AddressLine1 || ''} ${branchData.AddressLine2 || ''}, ${branchData.Location || 'Dubai'}, UAE`.trim(),
        trn: branchData.VATNumber ,
        phone: branchData.ContactNumber1 + (branchData.ContactNumber2 ? ',' + branchData.ContactNumber2 : '') + (branchData.ContactNumber3 ? ',' + branchData.ContactNumber3 : ''),
        email: branchData.Email ,
        vatNumber: branchData.VATNumber 
      };

      // Fetch customer address details
      let customerAddress1 = saleReturnData.ShipToAddress1 || '';
      let customerAddress2 = saleReturnData.ShipToAddress2 || '';
      let customerVATNumber = saleReturnData.GSTNumber || saleReturnData.VATNumber || '';
      if (saleReturnData.MasterId) {
        try {
          const customerResponse = await fetchData('ClientAndVendorApi', `/ClientAndVendorDetails/${saleReturnData.MasterId}`, token);
          const customerData = customerResponse.response_data?.Ledger || customerResponse.response_data || customerResponse || {};
          if (!customerAddress1) customerAddress1 = customerData.AddressLine1 || '';
          if (!customerAddress2) customerAddress2 = customerData.AddressLine2 || '';
          if (!customerVATNumber) customerVATNumber = customerData.VATNumber || '';
        } catch (err) {
          console.error('Error fetching customer details:', err);
        }
      }

      // Fetch sale date from original sale using SaleId
      let saleDate = '';
      if (saleReturnData.SaleId) {
        try {
          const saleResponse = await fetchData('SaleApi', `/${saleReturnData.SaleId}`, token);
          const saleData = saleResponse.response_data || saleResponse || {};
          const sale = saleData.Sale || saleData;
          saleDate = sale.BillDate || sale.billDate || '';
        } catch (err) {
          console.error('Error fetching sale date:', err);
        }
      }

      const saleReturnDataForPDF = {
        ...saleReturnData,
        saleReturnDetails: details,
        CustomerName: saleReturnData.CustomerName || saleReturnData.MasterName || '',
        VATNumber: customerVATNumber,
        BillPrefix: saleReturnData.BillPrefix || '',
        FinYear: saleReturnData.FinYear || '',
        BillNumber: saleReturnData.BillNumber,
        BillDate: saleReturnData.BillDate,
        SaleNo: saleReturnData.SaleNo || '',
        SaleDate: saleDate,
        InvoiceTypeName: saleReturnData.InvoiceTypeName || '',
        CurrencyName: saleReturnData.CurrencyName || '',
        TotalAmount: saleReturnData.TotalAmount || saleReturnData.GrandTotal || 0,
        TaxableAmount: saleReturnData.TaxableAmount || 0,
        VATAmount: saleReturnData.VATAmount || 0,
        FreightAmount: saleReturnData.FreightAmount || 0,
        FreightVATAmount: saleReturnData.FreightVATAmount || 0,
        PackingAmount: saleReturnData.PackingAmount || 0,
        PackingVATAmount: saleReturnData.PackingVATAmount || 0,
        RoundOff: saleReturnData.RoundOff || 0,
        CashAmount: saleReturnData.CashAmount || 0,
        UPIAmount: saleReturnData.UPIAmount || 0,
        CardAmount: saleReturnData.CardAmount || 0,
        BalanceAmount: saleReturnData.BalanceAmount || 0,
        ShipToAddress1: customerAddress1,
        ShipToAddress2: customerAddress2
      };

      await printSaleReturnPDF(saleReturnDataForPDF, companyDetails, items);
      setSuccess('Print dialog opened!');
    } catch (error) {
      console.error('Error printing:', error);
      setError('Failed to print credit note.');
    } finally {
      setPrintingItemId(null);
    }
  };

  // Track changes in edit form
  useEffect(() => {
    if (editModalOpen && originalEditData) {
      const formChanged = JSON.stringify(editFormData) !== JSON.stringify(originalEditData);
      const detailsChanged = JSON.stringify(editReturnDetails) !== JSON.stringify(originalEditDetails);
      setHasUnsavedChanges(formChanged || detailsChanged);
    }
  }, [editFormData, editReturnDetails, originalEditData, originalEditDetails, editModalOpen]);

  // Handle View
  const handleView = async (SaleReturn) => {
    setLoading(true);
    setLoadingMessage('Loading details...');
    try {
      let response;
      // Use different API endpoint for inactive items
      if (SaleReturn.IsActive === false) {
        response = await fetchInactiveSaleReturnDetails(SaleReturn.Id);
      } else {
        response = await fetchData('SaleReturnApi', `/${SaleReturn.Id}`, token);
        response = response.response_data || response;
      }

      setSelectedReturn(response.SaleReturn || response);
      setReturnDetails(response.SaleReturnDetails || []);
      setViewModalOpen(true);
    } catch (error) {
      console.error('Error fetching Sale return details:', error);
      setError('Failed to fetch Sale return details');
    }
    setLoading(false);
  };

  // Handle Edit
  const handleEdit = async (SaleReturn) => {
    setLoading(true);
    setLoadingMessage('Loading details...');
    try {
      // const response = await fetchData('SaleReturnApi', `/SaleReturn/${SaleReturn.Id}/${branchId}`, token);
      const response = await fetchData('SaleReturnApi', `/${SaleReturn.Id}`, token);
      const data = response.response_data || response;
      const returnData = data.SaleReturn || data;
      const details = data.SaleReturnDetails || [];

      setSelectedReturn(returnData);
      setReturnDetails(details);

      // Initialize edit return details with return quantities
      const initialEditDetails = details.map(detail => ({
        ...detail,
        ReturnQuantity: detail.Quantity || 0,
        // Use SaleQuantity from API response
        SaledQuantity: detail.SaleQuantity || detail.Quantity || 0,
        // Store original values for calculations
        OriginalRate: detail.Rate || 0,
        OriginalVATRate: detail.VATRate || 0
      }));
      setEditReturnDetails(initialEditDetails);

      setVATType(returnData.VATCalculationType || 1);

      const formData = {
        BillPrefix: returnData.BillPrefix || '',
        BillNumber: returnData.BillNumber || '',
        FinYear: returnData.FinYear || '',
        BillDate: returnData.BillDate?.split('T')[0] || '',
        SaleNo: returnData.SaleNo || '',
        MasterName: returnData.CustomerName || returnData.MasterName || '',
        Remarks: returnData.Remarks || '',
        VATNumber: returnData.VATNumber || '',
        InvoiceTypeName: returnData.InvoiceTypeName || '',
        VATCalculationType: returnData.VATCalculationType || 1,
        Amount: returnData.Amount || 0,
        DiscountAmount: returnData.DiscountAmount || 0,
        TaxableAmount: returnData.TaxableAmount || 0,
        // CGSTAmount: returnData.CGSTAmount || 0,
        // SGSTAmount: returnData.SGSTAmount || 0,
        VATAmount: returnData.VATAmount || 0,
        IGSTAmount: returnData.IGSTAmount || 0,
        TotalAmount: returnData.TotalAmount || 0,
        FreightAmount: returnData.FreightAmount || 0,
        FreightVATAmount: returnData.FreightVATAmount || 0,
        FreightVATRate: returnData.FreightVATRate || 5,
        PackingAmount: returnData.PackingAmount || 0,
        PackingVATAmount: returnData.PackingVATAmount || 0,
        PackingVATRate: returnData.PackingVATRate || 5,
        TCSAmount: returnData.TCSAmount || 0,
        PaidAmount: returnData.PaidAmount || 0,
        RoundOff: returnData.RoundOff || 0
      };

      setEditFormData(formData);

      // Store original data for change detection
      setOriginalEditData(JSON.parse(JSON.stringify(formData)));
      setOriginalEditDetails(JSON.parse(JSON.stringify(initialEditDetails)));
      setHasUnsavedChanges(false);

      // Calculate GST rates based on items after a short delay to ensure state is set
      setTimeout(() => {
        // Calculate the effective GST rates
        const maxItemVATRate = initialEditDetails.length > 0
          ? Math.max(...initialEditDetails.map(d => parseFloat(d.VATRate) || 0))
          : 0;
        const effectiveFreightVATRate = Math.max(5, maxItemVATRate);
        const effectivePackingVATRate = Math.max(5, maxItemVATRate);

        // Update freight and packing GST amounts with correct rates
        const freightAmount = parseFloat(formData.FreightAmount) || 0;
        const freightVATAmount = (freightAmount * effectiveFreightVATRate) / 100;
        const packingAmount = parseFloat(formData.PackingAmount) || 0;
        const packingVATAmount = (packingAmount * effectivePackingVATRate) / 100;

        setEditFormData(prev => ({
          ...prev,
          FreightVATRate: effectiveFreightVATRate,
          FreightVATAmount: freightVATAmount,
          PackingVATRate: effectivePackingVATRate,
          PackingVATAmount: packingVATAmount
        }));
      }, 100);

      setEditModalOpen(true);
    } catch (error) {
      console.error('Error fetching Sale return details:', error);
      setError('Failed to fetch Sale return details');
    }
    setLoading(false);
  };

  // Check if invoice type is IGST
  // B to B and B to C use CGST+SGST, others use IGST
  const isIGSTInvoice = () => {
    const invoiceType = (editFormData.InvoiceTypeName || '').toLowerCase();
    // If invoice type contains 'b to b' or 'b to c' or 'b2b' or 'b2c', use CGST+SGST
    if (invoiceType.includes('b to b') || invoiceType.includes('b to c') ||
      invoiceType.includes('b2b') || invoiceType.includes('b2c')) {
      return false; // Use CGST + SGST
    }
    // For IGST type or any other type, use IGST
    return invoiceType.includes('igst') || invoiceType.length > 0;
  };

  // Check if tax is including
  const isIncludingTax = () => vatType === 2;

  // Handle return quantity change
  const handleReturnQuantityChange = (index, value) => {
    console.log('Return Qty Change:', { index, value, editReturnDetails: editReturnDetails[index] });

    // Allow empty string for user to clear the field
    if (value === '') {
      const updatedDetails = [...editReturnDetails];
      updatedDetails[index] = {
        ...updatedDetails[index],
        ReturnQuantity: 0,
        Quantity: 0,
        Amount: 0,
        DiscountAmount: 0,
        TaxableAmount: 0,
        // CGSTAmount: 0,
        // SGSTAmount: 0,
        VATAmount: 0,
        IGSTAmount: 0,
        TotalAmount: 0
      };
      setEditReturnDetails(updatedDetails);
      calculateReturnTotals(updatedDetails);
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    const updatedDetails = [...editReturnDetails];
    const detail = updatedDetails[index];

    // Validate quantity
    if (numValue < 0) {
      setError('Return quantity cannot be negative');
      return;
    }
    if (numValue > detail.SaledQuantity) {
      setError(`Return quantity cannot exceed Saled quantity (${detail.SaledQuantity})`);
      return;
    }

    const returnQty = numValue;
    const rate = parseFloat(detail.Rate) || 0;
    const discountPercent = parseFloat(detail.DiscountPercentage) || 0;
    const vatRate = parseFloat(detail.VATRate) || 0;
    const useIGST = isIGSTInvoice();

    let taxableAmount, vatAmount, amount, discountAmount, baseAmount;

    if (isIncludingTax()) {
      // Rate includes GST - calculate backwards
      const totalWithVat = returnQty * rate;
      baseAmount = totalWithVat / (1 + vatRate / 100);
      discountAmount = (baseAmount * discountPercent) / 100;
      amount = baseAmount;
      taxableAmount = baseAmount - discountAmount;
      vatAmount = (taxableAmount * vatRate) / 100;

      updatedDetails[index] = {
        ...detail,
        ReturnQuantity: returnQty,
        Quantity: returnQty,
        Amount: Math.round(amount * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        TaxableAmount: Math.round(taxableAmount * 100) / 100,
        // CGSTAmount: useIGST ? 0 : gstAmount / 2,
        VATAmount: useIGST ? 0 : Math.round(vatAmount * 100) / 100,
        IGSTAmount: useIGST ? Math.round(vatAmount * 100) / 100 : 0,
        TotalAmount: Math.round((taxableAmount + vatAmount) * 100) / 100
      };
    } else {
      // Rate excludes GST - normal calculation
      baseAmount = returnQty * rate;
      discountAmount = (baseAmount * discountPercent) / 100;
      amount = baseAmount;
      taxableAmount = baseAmount - discountAmount;
      vatAmount = (taxableAmount * vatRate) / 100;

      updatedDetails[index] = {
        ...detail,
        ReturnQuantity: returnQty,
        Quantity: returnQty,
        Amount: Math.round(amount * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        TaxableAmount: Math.round(taxableAmount * 100) / 100,
        //CGSTAmount: useIGST ? 0 : gstAmount / 2,
        VATAmount: useIGST ? 0 : Math.round(vatAmount * 100) / 100,
        IGSTAmount: useIGST ? Math.round(vatAmount * 100) / 100 : 0,
        TotalAmount: Math.round((taxableAmount + vatAmount) * 100) / 100
      };
    }

    console.log('Updated Detail:', updatedDetails[index]);
    setEditReturnDetails(updatedDetails);
    calculateReturnTotals(updatedDetails);
  };

  // Handle discount percentage change
  const handleDiscountPercentChange = (index, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 0 || numValue > 100) {
      setError('Discount percentage must be between 0 and 100');
      return;
    }

    const updatedDetails = [...editReturnDetails];
    const detail = updatedDetails[index];

    const returnQty = parseFloat(detail.ReturnQuantity) || 0;
    const rate = parseFloat(detail.Rate) || 0;
    const vatRate = parseFloat(detail.VATRate) || 0;
    const useIGST = isIGSTInvoice();

    let taxableAmount, vatAmount, amount, discountAmount, baseAmount;

    if (isIncludingTax()) {
      const totalWithGst = returnQty * rate;
      baseAmount = totalWithGst / (1 + vatRate / 100);
      discountAmount = (baseAmount * numValue) / 100;
      amount = baseAmount;
      taxableAmount = baseAmount - discountAmount;
      vatAmount = (taxableAmount * vatRate) / 100;
    } else {
      baseAmount = returnQty * rate;
      discountAmount = (baseAmount * numValue) / 100;
      amount = baseAmount;
      taxableAmount = baseAmount - discountAmount;
      vatAmount = (taxableAmount * vatRate) / 100;
    }

    updatedDetails[index] = {
      ...detail,
      DiscountPercentage: numValue,
      Amount: Math.round(amount * 100) / 100,
      DiscountAmount: Math.round(discountAmount * 100) / 100,
      TaxableAmount: Math.round(taxableAmount * 100) / 100,
      // CGSTAmount: useIGST ? 0 : gstAmount / 2,
      VATAmount: useIGST ? 0 : Math.round(vatAmount * 100) / 100,
      IGSTAmount: useIGST ? Math.round(vatAmount * 100) / 100 : 0,
      TotalAmount: Math.round((taxableAmount + vatAmount) * 100) / 100
    };

    setEditReturnDetails(updatedDetails);
    calculateReturnTotals(updatedDetails);
  };

  // Handle discount amount change
  const handleDiscountAmountChange = (index, value) => {
    const numValue = parseFloat(value) || 0;

    const updatedDetails = [...editReturnDetails];
    const detail = updatedDetails[index];

    const returnQty = parseFloat(detail.ReturnQuantity) || 0;
    const rate = parseFloat(detail.Rate) || 0;
    const vatRate = parseFloat(detail.VATRate) || 0;
    const useIGST = isIGSTInvoice();

    let taxableAmount, vatAmount, amount, discountPercent, baseAmount;

    if (isIncludingTax()) {
      const totalWithVat = returnQty * rate;
      baseAmount = totalWithVat / (1 + vatRate / 100);
      amount = baseAmount;
      discountPercent = baseAmount > 0 ? (numValue / baseAmount) * 100 : 0;
      taxableAmount = baseAmount - numValue;
      vatAmount = (taxableAmount * vatRate) / 100;
    } else {
      baseAmount = returnQty * rate;
      amount = baseAmount;
      discountPercent = baseAmount > 0 ? (numValue / baseAmount) * 100 : 0;
      taxableAmount = baseAmount - numValue;
      vatAmount = (taxableAmount * vatRate) / 100;
    }

    updatedDetails[index] = {
      ...detail,
      DiscountPercentage: Math.round(discountPercent * 100) / 100,
      DiscountAmount: Math.round(numValue * 100) / 100,
      Amount: Math.round(amount * 100) / 100,
      TaxableAmount: Math.round(taxableAmount * 100) / 100,
      //CGSTAmount: useIGST ? 0 : vatAmount / 2,
      VATAmount: useIGST ? 0 : Math.round(vatAmount * 100) / 100,
      IGSTAmount: useIGST ? Math.round(vatAmount * 100) / 100 : 0,
      TotalAmount: Math.round((taxableAmount + vatAmount) * 100) / 100
    };

    setEditReturnDetails(updatedDetails);
    calculateReturnTotals(updatedDetails);
  };

  // Calculate return totals
  const calculateReturnTotals = (details, additionalCharges = {}) => {
    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
      //CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
      VATAmount: acc.VATAmount + (parseFloat(d.VATAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, VATAmount: 0, IGSTAmount: 0, TotalAmount: 0 });

    // Calculate freight and packing VAT rates
    // Use the maximum of default 5% OR the highest VAT rate among items
    const defaultFreightVATRate = 5;
    const defaultPackingVATRate = 5;
    const maxItemVATRate = details.length > 0 ? Math.max(...details.map(d => parseFloat(d.VATRate) || 0)) : 0;

    const effectiveFreightVATRate = Math.max(defaultFreightVATRate, maxItemVATRate);
    const effectivePackingVATRate = Math.max(defaultPackingVATRate, maxItemVATRate);

    // Use passed values or current state values
    const freightAmount = additionalCharges.FreightAmount !== undefined
      ? parseFloat(additionalCharges.FreightAmount) || 0
      : parseFloat(editFormData.FreightAmount) || 0;
    const freightVATAmount = (freightAmount * effectiveFreightVATRate) / 100;

    const packingAmount = additionalCharges.PackingAmount !== undefined
      ? parseFloat(additionalCharges.PackingAmount) || 0
      : parseFloat(editFormData.PackingAmount) || 0;
    const packingVATAmount = (packingAmount * effectivePackingVATRate) / 100;

    const tcsAmount = additionalCharges.TCSAmount !== undefined
      ? parseFloat(additionalCharges.TCSAmount) || 0
      : parseFloat(editFormData.TCSAmount) || 0;

    const grandTotal = totals.TotalAmount + freightAmount + freightVATAmount + packingAmount + packingVATAmount + tcsAmount;
    const roundOff = Math.round(grandTotal) - grandTotal;
    const totalAmount = Math.round(grandTotal);

    setEditFormData(prev => ({
      ...prev,
      ...totals,
      FreightAmount: freightAmount,
      FreightVATRate: effectiveFreightVATRate,
      FreightVATAmount: freightVATAmount,
      PackingAmount: packingAmount,
      PackingVATRate: effectivePackingVATRate,
      PackingVATAmount: packingVATAmount,
      TCSAmount: tcsAmount,
      RoundOff: roundOff,
      TotalAmount: totalAmount
    }));
  };

  // Handle VAT Type change
  const handleVATTypeChange = (value) => {
    const intValue = parseInt(value);
    setVATType(intValue);
    setEditFormData(prev => ({ ...prev, VATCalculationType: intValue }));

    // Recalculate all items with new VAT type
    const updatedDetails = editReturnDetails.map(detail => {
      const returnQty = parseFloat(detail.ReturnQuantity) || 0;
      const rate = parseFloat(detail.Rate) || 0;
      const vatRate = parseFloat(detail.VATRate) || 0;
      const useIGST = isIGSTInvoice();

      let taxableAmount, vatAmount, amount;

      if (intValue === 2) {
        // Including Tax
        const totalWithVat = returnQty * rate;
        taxableAmount = totalWithVat / (1 + vatRate / 100);
        amount = taxableAmount;
        vatAmount = (taxableAmount * vatRate) / 100;
      } else {
        // Excluding Tax
        amount = returnQty * rate;
        taxableAmount = amount;
        vatAmount = (taxableAmount * vatRate) / 100;
      }

      return {
        ...detail,
        Amount: amount,
        TaxableAmount: taxableAmount,
        //CGSTAmount: useIGST ? 0 : gstAmount / 2,
        VATAmount: useIGST ? 0 : vatAmount,
        IGSTAmount: useIGST ? vatAmount : 0,
        TotalAmount: taxableAmount + vatAmount
      };
    });

    setEditReturnDetails(updatedDetails);
    calculateReturnTotals(updatedDetails);
  };

  // Handle additional charges change
  const handleAdditionalChargeChange = (field, value) => {
    const numValue = parseFloat(value) || 0;

    // Create updated charges object
    const updatedCharges = {
      FreightAmount: field === 'FreightAmount' ? numValue : editFormData.FreightAmount,
      PackingAmount: field === 'PackingAmount' ? numValue : editFormData.PackingAmount,
      TCSAmount: field === 'TCSAmount' ? numValue : editFormData.TCSAmount
    };

    // Recalculate with new values
    if (editReturnDetails.length > 0) {
      calculateReturnTotals(editReturnDetails, updatedCharges);
    } else {
      setEditFormData(prev => ({ ...prev, [field]: numValue }));
    }
  };

  // Handle Save Edit
  const handleSaveEdit = async () => {
    setLoading(true);
    setLoadingMessage('Saving changes...');
    try {
      // Prepare the update payload with SaleReturn and SaleReturnDetails
      const updatePayload = {
        SaleReturn: {
          ...selectedReturn,
          BillDate: editFormData.BillDate,
          Remarks: editFormData.Remarks,
          VATCalculationType: editFormData.VATCalculationType,
          Amount: Math.round(parseFloat(editFormData.Amount || 0) * 100) / 100,
          DiscountAmount: Math.round(parseFloat(editFormData.DiscountAmount || 0) * 100) / 100,
          TaxableAmount: Math.round(parseFloat(editFormData.TaxableAmount || 0) * 100) / 100,
          //CGSTAmount: editFormData.CGSTAmount,
          VATAmount: Math.round(parseFloat(editFormData.VATAmount || 0) * 100) / 100,
          IGSTAmount: Math.round(parseFloat(editFormData.IGSTAmount || 0) * 100) / 100,
          TotalAmount: Math.round(parseFloat(editFormData.TotalAmount || 0) * 100) / 100,
          FreightAmount: Math.round(parseFloat(editFormData.FreightAmount || 0) * 100) / 100,
          FreightVATAmount: Math.round(parseFloat(editFormData.FreightVATAmount || 0) * 100) / 100,
          FreightVATRate: editFormData.FreightVATRate,
          PackingAmount: Math.round(parseFloat(editFormData.PackingAmount || 0) * 100) / 100,
          PackingVATAmount: Math.round(parseFloat(editFormData.PackingVATAmount || 0) * 100) / 100,
          PackingVATRate: editFormData.PackingVATRate,
          TCSAmount: Math.round(parseFloat(editFormData.TCSAmount || 0) * 100) / 100,
          PaidAmount: Math.round(parseFloat(editFormData.PaidAmount || 0) * 100) / 100,
          RoundOff: Math.round(parseFloat(editFormData.RoundOff || 0) * 100) / 100,
          BalanceAmount: Math.round((parseFloat(editFormData.TotalAmount || 0) - parseFloat(editFormData.PaidAmount || 0)) * 100) / 100
        },
        SaleReturnDetails: editReturnDetails.map(detail => ({
          ...detail,
          Quantity: detail.ReturnQuantity || detail.Quantity,
          Amount: Math.round(parseFloat(detail.Amount || 0) * 100) / 100,
          TaxableAmount: Math.round(parseFloat(detail.TaxableAmount || 0) * 100) / 100,
          //CGSTAmount: detail.CGSTAmount,
          VATAmount: Math.round(parseFloat(detail.VATAmount || 0) * 100) / 100,
          IGSTAmount: Math.round(parseFloat(detail.IGSTAmount || 0) * 100) / 100,
          TotalAmount: Math.round(parseFloat(detail.TotalAmount || 0) * 100) / 100
        }))
      };

      await updateData('SaleReturnApi', `${selectedReturn.Id}`, updatePayload, token);
      setSuccess('Sale return updated successfully');
      setHasUnsavedChanges(false);
      setEditModalOpen(false);
      fetchSaleReturns();
    } catch (error) {
      console.error('Error updating Sale return:', error);
      setError('Failed to update Sale return');
    }
    setLoading(false);
  };

  // Handle Cancel Edit - Check for unsaved changes
  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      setCancelConfirmOpen(true);
    } else {
      setEditModalOpen(false);
      setHasUnsavedChanges(false);
      setOriginalEditData(null);
      setOriginalEditDetails([]);
    }
  };

  // Confirm cancel without saving
  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setEditModalOpen(false);
    setHasUnsavedChanges(false);
    setOriginalEditData(null);
    setOriginalEditDetails([]);
  };

  // Close cancel confirmation and stay in edit mode
  const handleCloseCancelConfirm = () => {
    setCancelConfirmOpen(false);
  };

  // Handle Delete - Open confirmation modal
  const handleDelete = (SaleReturn) => {
    setDeleteItemId(SaleReturn.Id);
    setDeleteItemInfo({
      billNumber: SaleReturn.BillNumber,
      customer: SaleReturn.MasterName
    });
    setDeleteConfirmOpen(true);
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!deleteItemId) return;

    setDeleteConfirmOpen(false);
    setLoading(true);
    setLoadingMessage('Deleting Sale return...');
    try {
      await deleteData('SaleReturnApi', deleteItemId, token);
      setSuccess('Sale return deleted successfully');
      fetchSaleReturns();
    } catch (error) {
      console.error('Error deleting Sale return:', error);
      setError('Failed to delete Sale return');
    }
    setLoading(false);
    setDeleteItemId(null);
    setDeleteItemInfo({ billNumber: '', customer: '' });
  };

  // Close delete confirmation
  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setDeleteItemId(null);
    setDeleteItemInfo({ billNumber: '', customer: '' });
  };

  // Handle Activate - Open confirmation modal
  const handleActivate = (SaleReturn) => {
    setActivateItemId(SaleReturn.Id);
    setActivateItemInfo({
      billNumber: SaleReturn.BillNumber,
      customer: SaleReturn.MasterName
    });
    setActivateConfirmOpen(true);
  };

  // Confirm Activate
  const handleConfirmActivate = async () => {
    if (!activateItemId) return;

    setActivateConfirmOpen(false);
    setLoading(true);
    setLoadingMessage('Activating Sale return...');
    try {
      // Use PUT request to activate endpoint
      // await updateData('SaleReturnApi', `/${activateItemId}/activate`, {}, token);
      await activateData('SaleReturnApi', `${activateItemId}`, token);
      setSuccess('Sale return activated successfully');
      fetchSaleReturns();
    } catch (error) {
      console.error('Error activating Sale return:', error);
      setError('Failed to activate Sale return');
    }
    setLoading(false);
    setActivateItemId(null);
    setActivateItemInfo({ billNumber: '', customer: '' });
  };

  // Close activate confirmation
  const handleCloseActivateConfirm = () => {
    setActivateConfirmOpen(false);
    setActivateItemId(null);
    setActivateItemInfo({ billNumber: '', customer: '' });
  };

  // Handle Open Create Modal
  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
  };

  // Handle Close Create Modal
  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    window.location.reload();
  };

  // Handle Save Sale Return
  const handleSaveSaleReturn = async (formData) => {
    console.log('Saving Sale return:', formData);

    // Validation
    const validationErrors = [];

    if (!formData.billNo?.trim()) {
      validationErrors.push('Return Number is required');
    }

    if (!formData.billDate) {
      validationErrors.push('Return Date is required');
    }

    // Check if there are items with return quantity
    const itemsWithReturnQty = formData.items.filter(item => (item.rcvdQty || 0) > 0);
    if (itemsWithReturnQty.length === 0) {
      validationErrors.push('At least one item must have return quantity greater than 0');
    }

    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setLoadingMessage('Creating Sale return...');

    try {
      const vatCalculationType = formData.vatType === 'Including Tax' ? 2 : 1;

      const dataToSend = {
        SaleReturn: {
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          SaleId: parseInt(formData.SaleId) || 0,
          SaleNo: formData.SaleNo || '',
          SaleDate: formData.SaleDate || null,
          BillNumber: formData.billNo,
          BillDate: formData.billDate,
          MasterId: parseInt(formData.customerId),
          MasterName: formData.customer,
          InvoiceTypeId: 0,
          VATNumber: formData.VATNumber || '',
          VATCalculationType: vatCalculationType,
          VATCalculationTypeName: formData.vatType || 'Excluding Tax',
          Amount: parseFloat(formData.summary.subtotal) || 0,
          DiscountAmount: parseFloat(formData.summary.discount) || 0,
          TaxableAmount: parseFloat(formData.summary.taxable) || 0,
          //CGSTAmount: parseFloat(formData.summary.cgst) || 0,
          VATAmount: parseFloat(formData.summary.vatAmount) || 0,
          IGSTAmount: parseFloat(formData.summary.igst) || 0,
          FreightAmount: parseFloat(formData.additionalCharges.freight) || 0,
          FreightVATRate: parseFloat(formData.summary.effectiveVATRate) || 0,
          FreightVATAmount: parseFloat(formData.summary.freightVAT) || 0,
          PackingAmount: parseFloat(formData.additionalCharges.packing) || 0,
          PackingVATRate: parseFloat(formData.summary.effectiveVATRate) || 0,
          PackingVATAmount: parseFloat(formData.summary.packingVAT) || 0,
          TCSAmount: parseFloat(formData.additionalCharges.tcsAmount) || 0,
          RoundOff: parseFloat(formData.summary.roundOff) || 0,
          TotalAmount: parseFloat(formData.summary.total) || 0,
          PaidAmount: parseFloat(formData.additionalCharges.paidAmount) || 0,
          BalanceAmount: parseFloat(formData.summary.total - formData.additionalCharges.paidAmount) || 0,
          Remarks: formData.additionalCharges.remarks || '',
          IsActive: true
        },
        SaleReturnDetails: itemsWithReturnQty.map(item => ({
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          SaleReturnId: 0,
          ItemId: parseInt(item.itemId),
          ItemName: item.item || '',
          BatchNumber: item.batchNumber || '',
          SaleId: parseInt(formData.SaleId) || 0,
          SaleQuantity: parseFloat(item.SaledQty) || 0,
          Quantity: parseFloat(item.rcvdQty) || 0,
          ReturnStockQuantity: parseFloat(item.rcvdQty) || 0,
          Rate: parseFloat(item.rate) || 0,
          Amount: parseFloat((item.rcvdQty || 0) * (item.rate || 0)) || 0,
          DiscountPercentage: parseFloat(item.discPercent) || 0,
          DiscountAmount: parseFloat(item.discAmt) || 0,
          HSNCode: item.hsnCode || '',
          VATRate: parseFloat(item.vatPercent) || 0,
          TaxableAmount: parseFloat(item.taxable) || 0,
          //CGSTAmount: parseFloat(item.cgst) || 0,
          VATAmount: parseFloat(item.vatAmount) || 0,
          IGSTAmount: parseFloat(item.igst) || 0,
          TotalAmount: parseFloat(item.total) || 0,
          IsActive: true
        }))
      };

      console.log('Data to send:', dataToSend);

      await postData('SaleReturnApi', '', dataToSend, token);

      setSuccess('Sale return created successfully!');
      setCreateModalOpen(false);
      fetchSaleReturns();
    } catch (error) {
      console.error('Error creating Sale return:', error);
      setError('Failed to create Sale return.');
    }

    setLoading(false);
  };

  // Format currency
  const formatCurrency = (amount, currencyName) => {
    const name = currencyName || '';
    const getSymbol = (n) => {
      if (!n) return 'د.إ';
      const lower = n.toLowerCase().trim();
      const symbolMap = {
        'inr': '₹', 'rupee': '₹', 'indian rupee': '₹',
        'usd': '$', 'dollar': '$', 'us dollar': '$',
        'eur': '€', 'euro': '€',
        'gbp': '£', 'pound': '£', 'british pound': '£',
        'aed': 'د.إ', 'dirham': 'د.إ', 'uae dirham': 'د.إ',
        'sar': '﷼', 'riyal': '﷼', 'saudi riyal': '﷼',
        'jpy': '¥', 'yen': '¥',
        'cny': '¥', 'yuan': '¥', 'rmb': '¥',
        'aud': 'A$', 'australian dollar': 'A$',
        'cad': 'C$', 'canadian dollar': 'C$',
        'sgd': 'S$', 'singapore dollar': 'S$',
        'chf': 'CHF', 'swiss franc': 'CHF',
        'myr': 'RM', 'ringgit': 'RM',
        'bdt': '৳', 'taka': '৳',
        'pkr': '₨', 'pakistani rupee': '₨',
        'omr': 'OMR', 'omani rial': 'OMR',
        'qar': 'QR', 'qatari riyal': 'QR',
        'kwd': 'KD', 'kuwaiti dinar': 'KD',
        'bhd': 'BD', 'bahraini dinar': 'BD'
      };
      if (symbolMap[lower]) return symbolMap[lower];
      for (const [key, symbol] of Object.entries(symbolMap)) {
        if (lower.includes(key)) return symbol;
      }
      return n || 'د.إ';
    };
    const symbol = getSymbol(name);
    return `${symbol} ${(parseFloat(amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN');
  };

  // Filter Sale returns
  const filteredReturns = Array.isArray(SaleReturns) ? SaleReturns.filter(pr => {
    // Search filter
    if (searchTerm && !pr.BillNumber?.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !pr.MasterName?.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !pr.SaleNo?.toLowerCase().includes(searchTerm.toLowerCase())) return false;

    // Date range filter
    if (fromDate && new Date(pr.BillDate) < new Date(fromDate)) return false;
    if (toDate && new Date(pr.BillDate) > new Date(toDate)) return false;

    // Payment Status filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'paid' && (pr.BalanceAmount > 0 || pr.TotalAmount !== pr.PaidAmount)) return false;
      if (filterStatus === 'unpaid' && (pr.PaidAmount > 0 || pr.BalanceAmount === 0)) return false;
      if (filterStatus === 'partial' && (pr.PaidAmount === 0 || pr.BalanceAmount === 0)) return false;
    }

    // Active/Inactive filter
    if (filterActive !== 'all') {
      if (filterActive === 'active' && pr.IsActive !== true) return false;
      if (filterActive === 'inactive' && pr.IsActive !== false) return false;
    }

    return true;
  }) : [];

  // Pagination
  const totalPages = Math.ceil(filteredReturns.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = filteredReturns.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div style={{
      minWidth: '100%',
      background: '#f5f7fa',
      padding: '10px',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        padding: '12px',
        position: 'relative',
        color: 'black',
        fontFamily: 'Times New Romans',
      }}>
        {loading && <LoadingOverlay open={loading} message={loadingMessage} />}

        <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
        </Snackbar>

        {/* Header Section with Title */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          paddingBottom: '8px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Assignment sx={{ fontSize: 24, color: '#0f3460' }} />
            <div>
              <h4 style={{
                fontSize: '17px',
                fontWeight: '700',
                margin: 0,
                letterSpacing: '-0.3px',
                fontFamily: 'Times New Romans',
                textTransform: 'uppercase'
              }}>
                <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Sale Return Management</span>
              </h4>
            </div>
          </div>
        </div>

        {/* Date Filters, Search and Create Button Row - Horizontally Scrollable */}
        <div style={{
          marginBottom: '12px',
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin'
        }}>
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'flex-end',
            minWidth: 'fit-content',
            paddingBottom: '4px'
          }}>
            {/* Filter Icon - Mobile Only */}
            {isMobile && (
              <Tooltip title="Filters">
                <IconButton
                  size="small"
                  onClick={() => setShowMobileFilters(!showMobileFilters)}
                  sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    border: '1px solid #0f3460',
                    color: '#0f3460',
                    backgroundColor: showMobileFilters ? '#f0f4ff' : 'transparent',
                    '&:hover': {
                      backgroundColor: '#f0f4ff',
                    },
                  }}
                >
                  <FilterList sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* From Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '100px' : '120px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
              <TextField
                type="date"
                size="small"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                sx={{
                  width: isMobile ? '115px' : '140px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '11px',
                    height: '32px',
                    '&:hover fieldset': { borderColor: '#0f3460' },
                    '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                  },
                }}
              />
            </div>

            {/* To Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '100px' : '120px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
              <TextField
                type="date"
                size="small"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                sx={{
                  width: isMobile ? '115px' : '140px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '11px',
                    height: '32px',
                    '&:hover fieldset': { borderColor: '#0f3460' },
                    '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                  },
                }}
              />
            </div>

            {/* Desktop Filters - Hidden on Mobile */}
            {!isMobile && (
              <>
                {/* Search */}
                <TextField
                  size="small"
                  placeholder="Search by Bill No, customer..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  sx={{
                    minWidth: '200px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#0f3460' },
                      '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                    },
                  }}
                />

                {/* Status Filter */}
                <TextField
                  select
                  size="small"
                  value={filterStatus}
                  onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                  sx={{
                    width: '120px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#0f3460' },
                      '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                    },
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                </TextField>

                {/* Active Filter */}
                <TextField
                  select
                  size="small"
                  value={filterActive}
                  onChange={(e) => { setFilterActive(e.target.value); setCurrentPage(1); }}
                  sx={{
                    width: '100px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#0f3460' },
                      '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                    },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
              </>
            )}

            {/* Refresh Button */}
            {isMobile ? (
              <Tooltip title="Refresh">
                <IconButton
                  size="small"
                  onClick={fetchSaleReturns}
                  disabled={loading}
                  sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    border: '1px solid #0f3460',
                    color: '#0f3460',
                    '&:hover': {
                      backgroundColor: '#f0f4ff',
                    },
                  }}
                >
                  <Refresh sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="outlined"
                size="small"
                onClick={fetchSaleReturns}
                disabled={loading}
                startIcon={<Refresh />}
                sx={{
                  height: '32px',
                  fontSize: '11px',
                  textTransform: 'none',
                  borderRadius: '6px',
                  borderColor: '#0f3460',
                  color: '#0f3460',
                  '&:hover': {
                    borderColor: '#5568d3',
                    backgroundColor: '#f0f4ff',
                  },
                }}
              >
                Refresh
              </Button>
            )}

            {/* Add Button */}
            {canCreateTransaction && (isMobile ? (
              <Tooltip title="Add Return">
                <IconButton
                  size="small"
                  onClick={() => handleOpenCreateModal()}
                  disabled={loading}
                  sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    backgroundColor: '#0f3460',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#5568d3',
                    },
                  }}
                >
                  <Add sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={() => handleOpenCreateModal()}
                disabled={loading}
                sx={{
                  height: '32px',
                  fontSize: '11px',
                  textTransform: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#0f3460',
                  '&:hover': {
                    backgroundColor: '#5568d3',
                  },
                }}
              >
                Add Return
              </Button>
            ))}
          </div>
        </div>

        {/* Mobile Filters Collapse */}
        {isMobile && showMobileFilters && (
          <Box sx={{
            marginBottom: '12px',
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search by Bill No, customer..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#0f3460' },
                      '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={filterStatus}
                  onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#0f3460' },
                      '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                    },
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={filterActive}
                  onChange={(e) => { setFilterActive(e.target.value); setCurrentPage(1); }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#0f3460' },
                      '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                    },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Table Section */}
        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', height: '27px', textTransform: 'uppercase' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>Sl No.</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>Return No</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>Sale No</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>customer</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>Total Amount</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>Balance Amount</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', textAlign: 'right', height: '27px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ padding: '40px', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ padding: '40px', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>
                    No Sale returns found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((pr, index) => (
                  <TableRow
                    key={pr.Id}
                    sx={{
                      backgroundColor: pr.IsActive ? 'transparent' : '#fee2e2',
                      '&:hover': { backgroundColor: pr.IsActive ? '#f9fafb' : '#fecaca' },
                      borderBottom: '1px solid #e5e7eb',
                      height: '27px'
                    }}
                  >
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', color: '#6b7280', fontWeight: 500, height: '27px' }}>
                      {startIndex + index + 1}
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', height: '27px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Assignment sx={{ fontSize: 18, color: '#1e40af' }} />
                        <span style={{ fontWeight: 600, color: '#1e40af' }}>
                          {pr.BillPrefix}/{pr.BillNumber}/{pr.FinYear}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', color: '#374151', height: '27px' }}>
                      {formatDate(pr.BillDate)}
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', color: '#374151', fontWeight: 500, height: '27px' }}>
                      {pr.SaleNo || '-'}
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', color: '#374151', height: '27px' }}>
                      {pr.CustomerName}
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', height: '27px' }}>
                      <Chip
                        label={formatCurrency(pr.TotalAmount, pr.CurrencyName)}
                        size="small"
                        sx={{
                          bgcolor: '#d1fae5',
                          color: '#065f46',
                          fontWeight: 600,
                          fontSize: '11px',
                          height: '20px'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', height: '27px' }}>
                      <Chip
                        label={formatCurrency(pr.BalanceAmount, pr.CurrencyName)}
                        size="small"
                        sx={{
                          bgcolor: pr.BalanceAmount > 0 ? '#fee2e2' : '#d1fae5',
                          color: pr.BalanceAmount > 0 ? '#991b1b' : '#065f46',
                          fontWeight: 600,
                          fontSize: '11px',
                          height: '20px'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', height: '27px' }}>
                      <Chip
                        label={pr.IsActive ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: pr.IsActive ? '#d1fae5' : '#fee2e2',
                          color: pr.IsActive ? '#065f46' : '#991b1b',
                          fontWeight: 600,
                          fontSize: '11px',
                          height: '20px'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', textAlign: 'right', height: '27px' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                        {pr.IsActive ? (
                          <>
                            <Tooltip title="View">
                              <Button
                                size="small"
                                onClick={() => handleView(pr)}
                                style={{
                                  minWidth: '22px',
                                  height: '22px',
                                  borderRadius: '6px',
                                  color: 'white',
                                  padding: '2px',
                                  boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                  border: 'none',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                👁️
                              </Button>
                            </Tooltip>
                            <Tooltip title="Print">
                              <Button
                                size="small"
                                onClick={() => handlePrint(pr.Id)}
                                disabled={printingItemId === pr.Id}
                                style={{
                                  minWidth: '22px',
                                  height: '22px',
                                  borderRadius: '6px',
                                  color: 'white',
                                  padding: '2px',
                                  boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                  border: 'none',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                🖨️
                              </Button>
                            </Tooltip>
                            {canEditTransaction && (
                            <Tooltip title="Edit">
                              <Button
                                size="small"
                                onClick={() => handleEdit(pr)}
                                style={{
                                  minWidth: '22px',
                                  height: '22px',
                                  borderRadius: '6px',
                                  color: 'white',
                                  padding: '2px',
                                  boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                  border: 'none',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                ✏️
                              </Button>
                            </Tooltip>
                            )}
                            {canDeleteTransaction && (
                              <Tooltip title="Delete">
                                <Button
                                  size="small"
                                  onClick={() => handleDelete(pr)}
                                  style={{
                                    minWidth: '22px',
                                    height: '22px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    padding: '2px',
                                    boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                    border: 'none',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}
                                >
                                  🗑️
                                </Button>
                              </Tooltip>
                            )}
                          </>
                        ) : (
                          <>
                            <Tooltip title="View">
                              <Button
                                size="small"
                                onClick={() => handleView(pr)}
                                style={{
                                  minWidth: '22px',
                                  height: '22px',
                                  borderRadius: '6px',
                                  color: 'white',
                                  padding: '2px',
                                  boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
                                  border: 'none',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                👁️
                              </Button>
                            </Tooltip>
                            <Tooltip title="Print">
                              <Button
                                size="small"
                                onClick={() => handlePrint(pr.Id)}
                                disabled={printingItemId === pr.Id}
                                style={{
                                  minWidth: '22px',
                                  height: '22px',
                                  borderRadius: '6px',
                                  color: 'white',
                                  padding: '2px',
                                  boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
                                  border: 'none',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                🖨️
                              </Button>
                            </Tooltip>
                            <Tooltip title="Activate">
                              <Button
                                size="small"
                                onClick={() => handleActivate(pr)}
                                style={{
                                  minWidth: '22px',
                                  height: '22px',
                                  borderRadius: '6px',
                                  color: 'white',
                                  padding: '2px',
                                  boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                  border: 'none',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                ✅
                              </Button>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {filteredReturns.length > 0 && (
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            marginTop: '12px',
            padding: '8px 0',
            gap: { xs: '12px', sm: '0' }
          }}>
            <Typography variant="body2" sx={{ fontSize: '11px', color: '#6b7280', textAlign: { xs: 'center', sm: 'left' } }}>
              Showing {startIndex + 1} to {Math.min(endIndex, filteredReturns.length)} of {filteredReturns.length} entries
            </Typography>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: '8px',
              alignItems: 'center',
              justifyContent: { xs: 'center', sm: 'flex-end' }
            }}>
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" sx={{ fontSize: '11px', color: '#6b7280' }}>Rows per page:</Typography>
                <TextField
                  select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  sx={{
                    width: '70px',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '11px',
                      height: '28px',
                    },
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </TextField>
              </Box>
              <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  size="small"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  sx={{
                    minWidth: { xs: '50px', sm: '60px' },
                    height: '28px',
                    fontSize: '11px',
                    textTransform: 'none',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    color: '#374151',
                    '&:hover': { backgroundColor: '#f9fafb' },
                    '&:disabled': { color: '#d1d5db' }
                  }}
                >
                  First
                </Button>
                <Button
                  size="small"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  sx={{
                    minWidth: { xs: '50px', sm: '60px' },
                    height: '28px',
                    fontSize: '11px',
                    textTransform: 'none',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    color: '#374151',
                    '&:hover': { backgroundColor: '#f9fafb' },
                    '&:disabled': { color: '#d1d5db' }
                  }}
                >
                  Prev
                </Button>
                {Array.from({ length: totalPages }, (_, i) => {
                  const page = i + 1;
                  // Show fewer page numbers on mobile
                  const isMobile = window.innerWidth < 600;
                  if (isMobile) {
                    if (page === currentPage || page === 1 || page === totalPages) {
                      return (
                        <Button
                          key={page}
                          size="small"
                          onClick={() => handlePageChange(page)}
                          sx={{
                            minWidth: '32px',
                            height: '28px',
                            fontSize: '11px',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb',
                            backgroundColor: currentPage === page ? '#0f3460' : 'white',
                            color: currentPage === page ? 'white' : '#374151',
                            '&:hover': {
                              backgroundColor: currentPage === page ? '#5568d3' : '#f9fafb'
                            }
                          }}
                        >
                          {page}
                        </Button>
                      );
                    } else if (page === currentPage - 1 || page === currentPage + 1) {
                      return <span key={page} style={{ padding: '0 2px', color: '#9ca3af' }}>...</span>;
                    }
                    return null;
                  } else {
                    // Desktop view - show more pages
                    if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                      return (
                        <Button
                          key={page}
                          size="small"
                          onClick={() => handlePageChange(page)}
                          sx={{
                            minWidth: '32px',
                            height: '28px',
                            fontSize: '11px',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb',
                            backgroundColor: currentPage === page ? '#0f3460' : 'white',
                            color: currentPage === page ? 'white' : '#374151',
                            '&:hover': {
                              backgroundColor: currentPage === page ? '#5568d3' : '#f9fafb'
                            }
                          }}
                        >
                          {page}
                        </Button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} style={{ padding: '0 4px', color: '#9ca3af' }}>...</span>;
                    }
                    return null;
                  }
                })}
                <Button
                  size="small"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  sx={{
                    minWidth: { xs: '50px', sm: '60px' },
                    height: '28px',
                    fontSize: '11px',
                    textTransform: 'none',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    color: '#374151',
                    '&:hover': { backgroundColor: '#f9fafb' },
                    '&:disabled': { color: '#d1d5db' }
                  }}
                >
                  Next
                </Button>
                <Button
                  size="small"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  sx={{
                    minWidth: { xs: '50px', sm: '60px' },
                    height: '28px',
                    fontSize: '11px',
                    textTransform: 'none',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    color: '#374151',
                    '&:hover': { backgroundColor: '#f9fafb' },
                    '&:disabled': { color: '#d1d5db' }
                  }}
                >
                  Last
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </div>

      {/* Edit Modal */}
      <SaleReturnModal
        open={editModalOpen}
        onClose={handleCancelEdit}
        modalAction="edit"
        selectedSaleReturn={selectedReturn}
        saleReturnDetails={returnDetails}
        companyId={companyId}
        branchId={branchId}
        token={token}
        customers={customers}
        setLoading={setLoading}
        setLoadingMessage={setLoadingMessage}
        setError={setError}
        setSuccess={setSuccess}
        fetchSaleReturns={fetchSaleReturns}
        setCreateModalOpen={setEditModalOpen}
        userId={userId}
      />

      {/* View Modal */}
      <SaleReturnModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        modalAction="view"
        selectedSaleReturn={selectedReturn}
        saleReturnDetails={returnDetails}
        companyId={companyId}
        branchId={branchId}
        token={token}
        customers={customers}
        setLoading={setLoading}
        setLoadingMessage={setLoadingMessage}
        setError={setError}
        setSuccess={setSuccess}
        fetchSaleReturns={fetchSaleReturns}
        setCreateModalOpen={setViewModalOpen}
        userId={userId}
      />

      {/* Cancel Confirmation Modal */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={handleCloseCancelConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fef3c7',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Warning sx={{ color: '#f59e0b', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#92400e', fontSize: '1.125rem' }}>
            Unsaved Changes
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="body1" sx={{ color: '#64748b', fontSize: '0.938rem', lineHeight: 1.6 }}>
            You have unsaved changes. Are you sure you want to cancel without saving? All changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          p: 2,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          gap: 1.5
        }}>
          <Button
            variant="outlined"
            onClick={handleCloseCancelConfirm}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              borderColor: '#64748b',
              color: '#64748b',
              '&:hover': {
                borderColor: '#475569',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Keep Editing
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCancel}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626, #b91c1c)'
              }
            }}
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fef2f2',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Warning sx={{ color: '#ef4444', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b', fontSize: '1.125rem' }}>
            Delete Sale Return
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="body1" sx={{ color: '#64748b', fontSize: '0.938rem', lineHeight: 1.6, mb: 2 }}>
            Are you sure you want to delete this Sale return?
          </Typography>
          {deleteItemInfo.billNumber && (
            <Box sx={{
              bgcolor: '#f8fafc',
              borderRadius: 1.5,
              p: 2,
              border: '1px solid #e2e8f0'
            }}>
              <Grid container spacing={1}>
                <Grid item xs={5}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                    Return No:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700, fontSize: '0.875rem' }}>
                    {deleteItemInfo.billNumber}
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                    customer:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600, fontSize: '0.875rem' }}>
                    {deleteItemInfo.customer}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.813rem', mt: 2, fontWeight: 600 }}>
            ⚠️ This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          p: 2,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          gap: 1.5
        }}>
          <Button
            variant="outlined"
            onClick={handleCloseDeleteConfirm}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              borderColor: '#64748b',
              color: '#64748b',
              '&:hover': {
                borderColor: '#475569',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626, #b91c1c)'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation Modal */}
      <Dialog
        open={activateConfirmOpen}
        onClose={handleCloseActivateConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#d1fae5',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <CheckCircle sx={{ color: '#10b981', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#065f46', fontSize: '1.125rem' }}>
            Activate Sale Return
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="body1" sx={{ color: '#64748b', fontSize: '0.938rem', lineHeight: 1.6, mb: 2 }}>
            Are you sure you want to activate this Sale return?
          </Typography>
          {activateItemInfo.billNumber && (
            <Box sx={{
              bgcolor: '#f8fafc',
              borderRadius: 1.5,
              p: 2,
              border: '1px solid #e2e8f0'
            }}>
              <Grid container spacing={1}>
                <Grid item xs={5}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                    Return No:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700, fontSize: '0.875rem' }}>
                    {activateItemInfo.billNumber}
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                    customer:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600, fontSize: '0.875rem' }}>
                    {activateItemInfo.customer}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <Typography variant="body2" sx={{ color: '#10b981', fontSize: '0.813rem', mt: 2, fontWeight: 600 }}>
            ✓ This will restore the Sale return to active status.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          p: 2,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          gap: 1.5
        }}>
          <Button
            variant="outlined"
            onClick={handleCloseActivateConfirm}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              borderColor: '#64748b',
              color: '#64748b',
              '&:hover': {
                borderColor: '#475569',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmActivate}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669, #047857)'
              }
            }}
          >
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Sale Return Modal */}
      <SaleReturnModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        onSave={handleSaveSaleReturn}
        modalAction="create"
        companyId={companyId}
        branchId={branchId}
        token={token}
        customers={customers}
        setLoading={setLoading}
        setLoadingMessage={setLoadingMessage}
        setError={setError}
        setSuccess={setSuccess}
        fetchSaleReturns={fetchSaleReturns}
        setCreateModalOpen={setCreateModalOpen}
        userId={userId}
      />
    </div>
  );
};

export default SaleReturnAgainstIndex;
